import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyCourses, getMyProgress } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookOpen, ArrowRight, Users, Calendar, GraduationCap } from 'lucide-react';

/**
 * The student's own enrolled courses.
 *
 * This is the way into coursework. The catalog is a separate, browse-only
 * reference list — since registration became registrar-controlled, a student
 * cannot act on a course they are not already enrolled in.
 */
export default function StudentMyCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [coursesRes, progressRes] = await Promise.all([
          getMyCourses(),
          getMyProgress().catch(() => ({ data: { progress: {} } })),
        ]);
        if (cancelled) return;
        setCourses(coursesRes.data || []);
        setProgress(progressRes.data?.progress || {});
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Your courses could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-56 animate-pulse rounded-xl bg-white" />
        ))}
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          My Courses
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">
          {courses.length
            ? `You are enrolled in ${courses.length} course${courses.length === 1 ? '' : 's'}.`
            : 'The courses you are registered in appear here.'}
        </p>
      </div>

      {error && <p className="text-sm text-rose-700">{error}</p>}

      {courses.length === 0 && !error ? (
        <div className="py-16 text-center">
          <GraduationCap className="mx-auto mb-3 h-12 w-12 text-[#062B1A]/15" />
          <p className="mb-1 text-[#062B1A]/60">No courses yet</p>
          <p className="mx-auto mb-4 max-w-sm text-sm text-[#062B1A]/40">
            Course registration is handled by the registrar. Once you are registered, your courses
            appear here automatically.
          </p>
          <Button variant="outline" onClick={() => navigate('/student/catalog')}>
            Browse the course catalog
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {courses.map((course) => {
            const pct = progress[course.id] || 0;
            return (
              <Card
                key={course.id}
                className="card-hover group cursor-pointer overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm"
                onClick={() => navigate(`/student/courses/${course.id}`)}
                data-testid={`my-course-card-${course.id}`}
              >
                <div className="relative flex h-28 items-center justify-center bg-gradient-to-br from-[#062B1A] to-[#0A3B24]">
                  <BookOpen className="h-9 w-9 text-[#D4AF37]/60" />
                  {course.term && (
                    <Badge className="absolute right-3 top-3 border-0 bg-white/20 text-[10px] text-white">
                      <Calendar className="mr-1 h-3 w-3" />
                      {course.term}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {course.course_code && (
                      <Badge variant="outline" className="border-[#062B1A]/20 text-[10px] text-[#062B1A]/70">
                        {course.course_code}
                      </Badge>
                    )}
                    <Badge variant="outline" className="border-[#D4AF37]/30 text-[10px] text-[#D4AF37]">
                      {course.category}
                    </Badge>
                  </div>

                  <h3
                    className="mb-1 line-clamp-2 text-sm font-semibold text-[#062B1A]"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {course.title}
                  </h3>
                  <p className="mb-3 line-clamp-2 text-xs text-[#062B1A]/50">{course.description}</p>

                  <div className="mb-3">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[11px] font-medium text-[#062B1A]/50">Progress</span>
                      <span className="text-[11px] font-semibold text-[#D4AF37]">{pct}%</span>
                    </div>
                    <Progress value={pct} className="h-1.5 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]" />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-[#062B1A]/40">
                      <span className="flex items-center gap-1">
                        <BookOpen className="h-3 w-3" />
                        {course.lessons_count || 0} lessons
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {course.enrollment_count || 0}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      className="btn-hover h-7 bg-[#062B1A] text-xs text-white hover:bg-[#041B10]"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/student/courses/${course.id}`);
                      }}
                    >
                      {pct > 0 ? 'Continue' : 'Start'} <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
